import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { fetchConsumables } from '../../../utils/api';

/**
 * MUI Components
 */
import { CircularProgress, Alert, Autocomplete, TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';
import useToken from '../../../hooks/useToken';

const ConsumablesSelect = ({ onSelect, value }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const [consumables, setConsumables] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filteredConsumables, setFilteredConsumables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedConsumable, setSelectedConsumable] = useState(null);

    const NO_CATEGORY_LABEL = '(Sin categoría)';

    const handleCategorySelect = (event) => {
        setSelectedCategory(event.target.value);
        // console.debug('Category set to', event.target.value);
    };

    const handleConsumableSelect = (event, consumable) => {
        setSelectedConsumable(consumable);
        onSelect(consumable);
    };

    useEffect(() => {
        const controller = new AbortController(); // Create a new AbortController
        const { signal } = controller;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await fetchConsumables(token, search, signal);
                setConsumables(data);

                // Get unique categories
                const uniqueCategories = [...new Set(data.filter(c => c.category).map(c => c.category.name))];

                // Add a category for consumables without a category
                if (data.some(c => !c.category)) {
                    uniqueCategories.push(NO_CATEGORY_LABEL);
                }

                setCategories(uniqueCategories);

                // Set the selected category if value is provided
                if (value) {
                    const preselectedConsumable = data.find(c => c.id === value);
                    if (preselectedConsumable) {
                        console.log('Preselecting consumable...');
                        setSelectedCategory(preselectedConsumable.category?.name || '');
                        setSelectedConsumable(preselectedConsumable);
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        // Clean up function
        return () => {
            controller.abort(); // Abort the fetch request on component unmount
        };
    }, [search, token]);

    useEffect(() => {
        const filtered = consumables.filter(c =>
            (!selectedCategory || (selectedCategory === NO_CATEGORY_LABEL ? !c.category : c.category?.name === selectedCategory)) &&
            (!search || c.name.toLowerCase().includes(search.toLowerCase()))
        );
        setFilteredConsumables(filtered);
    }, [consumables, search, selectedCategory]);

    return (
        <>
            {loading && <CircularProgress />}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && (
                <>
                    <FormControl fullWidth>
                        <InputLabel id="category-label">{t('Category')}</InputLabel>
                        <Select
                            labelId="category-label"
                            id="category-select"
                            value={selectedCategory}
                            onChange={handleCategorySelect}
                            label={t('Category')}
                        >
                            <MenuItem value=""><em>{t('All')}</em></MenuItem>
                            {categories.map(category => (
                                <MenuItem key={category} value={category}>{category}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        sx={{ marginTop: 2 }}
                        options={filteredConsumables}
                        value={selectedConsumable}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('Select consumable')}
                                variant="outlined"
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        )}
                        onChange={handleConsumableSelect}
                        filterSelectedOptions
                    />
                </>
            )}
        </>
    );
};

ConsumablesSelect.propTypes = {
    onSelect: PropTypes.func.isRequired,
    value: PropTypes.number,
};

export default ConsumablesSelect;